import { type ReactElement, type ReactNode, useMemo } from "react";

import {
	AccordionItem,
	AccordionItemSize,
} from "@/components/Molecules/AccordionItem/AccordionItem";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { useDynamicFormContext } from "@/lib/dynamicForms/contexts/DynamicFormContext";
import type { FieldDefinition } from "@/services/form-definition/form-definition-core.types";

import type { LayoutElement } from "../schemas/layout.schema";
import {
	collectLayoutFieldProgressRefs,
	countVisibleSectionCompletion,
} from "../utils/section-completion.util";

export interface LayoutGroupAccordionProps {
	/** Section label (without the "Section: " prefix). */
	label?: string;
	defaultExpanded: boolean;
	/** Controlled open state (used when expand-all coordinates multiple sections). */
	isExpanded?: boolean;
	/** Fires when the user toggles this section. */
	onExpandedChange?: (expanded: boolean) => void;
	/** Layout nodes used for completion progress (typically the rendered group children). */
	progressNodes: LayoutElement[];
	fieldDefinitionsById: ReadonlyMap<string, FieldDefinition>;
	fieldDefinitionsByName: ReadonlyMap<string, FieldDefinition>;
	children: ReactNode;
}

/**
 * Job workspace form section accordion (Figma AccordionItem 4893:62536).
 * Title: "Section: {label}"; subtitle: "{completed} of {total} Completed"
 * where `total` is the count of currently visible fields in the group.
 */
export function LayoutGroupAccordion({
	label,
	defaultExpanded,
	isExpanded,
	onExpandedChange,
	progressNodes,
	fieldDefinitionsById,
	fieldDefinitionsByName,
	children,
}: LayoutGroupAccordionProps): ReactElement {
	const { t } = useAppTranslation();
	const ctx = useDynamicFormContext();
	const formValues = ctx?.formValues ?? {};
	const fieldStates = ctx?.fieldStates ?? {};

	const progressRefs = useMemo(
		() => progressNodes.flatMap(collectLayoutFieldProgressRefs),
		[progressNodes],
	);

	const { completed, total } = useMemo(
		() =>
			countVisibleSectionCompletion({
				progressRefs,
				formValues,
				fieldStates,
				fieldDefinitionsById,
				fieldDefinitionsByName,
			}),
		[
			fieldDefinitionsById,
			fieldDefinitionsByName,
			fieldStates,
			formValues,
			progressRefs,
		],
	);

	const sectionTitle =
		label?.trim() || t("main.jobs.workspace.form.sectionUntitled");

	const primaryText = t("main.jobs.workspace.form.sectionHeading", {
		title: sectionTitle,
	});

	const secondaryText =
		total > 0
			? t("main.jobs.workspace.form.sectionCompleted", {
					completed,
					total,
				})
			: undefined;

	return (
		<AccordionItem
			size={AccordionItemSize.Sm}
			primaryText={primaryText}
			secondaryText={secondaryText}
			showSecondaryText={Boolean(secondaryText)}
			showBadge={false}
			showLeadingSlot={false}
			defaultExpanded={defaultExpanded}
			isExpanded={isExpanded}
			onExpandedChange={onExpandedChange}
			className="w-full"
		>
			{children}
		</AccordionItem>
	);
}
